import type { ResourceBudget, RoleBudget, PersonBudget } from '@/types/budget.types';
import { RateCardStorageService } from './rateCardStorage.service';
import { MockOrgDefaultsService } from './mockOrgDefaults.service';

export interface MonthlyCalculation {
  month: string; // YYYY-MM format
  hours: number;
  costRate: number;
  billRate: number;
  cost: number;
  revenue: number;
  margin: number;
}

export interface RoleCalculation {
  roleId: string;
  roleName: string;
  totalHours: number;
  totalCost: number;
  totalRevenue: number;
  averageCostRate: number;
  averageBillRate: number;
  margin: number;
  monthlyBreakdown: MonthlyCalculation[];
}

export interface BudgetCalculationResult {
  totalLabourCost: number;
  totalRevenue: number;
  totalMargin: number;
  marginPercentage: number;
  roleBreakdown: RoleCalculation[];
  calculatedAt: string;
}

export class BudgetCalculationService {
  /**
   * Calculate Labour cost and Revenue from Resource Budget
   */
  static calculateBudget(
    projectId: string,
    resourceBudget: ResourceBudget
  ): BudgetCalculationResult {
    console.log('[BudgetCalculationService] Calculating budget for project:', projectId);
    console.log('[BudgetCalculationService] Resource budget:', resourceBudget);

    const roleBreakdown: RoleCalculation[] = [];
    let totalLabourCost = 0;
    let totalRevenue = 0;

    // Process each role in the resource budget
    if (resourceBudget.byRole && resourceBudget.byRole.length > 0) {
      for (const roleBudget of resourceBudget.byRole) {
        const roleCalc = this.calculateRoleBudget(projectId, roleBudget, resourceBudget);
        roleBreakdown.push(roleCalc);
        totalLabourCost += roleCalc.totalCost;
        totalRevenue += roleCalc.totalRevenue;
      }
    }

    const totalMargin = totalRevenue - totalLabourCost;
    const marginPercentage = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

    const result: BudgetCalculationResult = {
      totalLabourCost: Math.round(totalLabourCost * 100) / 100,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalMargin: Math.round(totalMargin * 100) / 100,
      marginPercentage: Math.round(marginPercentage * 100) / 100,
      roleBreakdown,
      calculatedAt: new Date().toISOString(),
    };

    console.log('[BudgetCalculationService] Calculation result:', result);
    return result;
  }

  /**
   * Calculate budget for a specific role
   */
  private static calculateRoleBudget(
    projectId: string,
    roleBudget: RoleBudget,
    resourceBudget: ResourceBudget
  ): RoleCalculation {
    console.log('[BudgetCalculationService] Calculating role budget:', roleBudget.role);

    // Get the rate for this role (respects hierarchy: person override > project override > org default)
    const roleRates = RateCardStorageService.getAllRoleRates(projectId);
    console.log('[BudgetCalculationService] All role rates:', roleRates.map(r => ({ name: r.roleName, cost: r.hourlyCostRate, bill: r.hourlyBillRate })));
    console.log('[BudgetCalculationService] Looking for role:', roleBudget.role);
    const roleRate = roleRates.find(r => r.roleName === roleBudget.role);
    console.log('[BudgetCalculationService] Found rate:', roleRate ? { name: roleRate.roleName, cost: roleRate.hourlyCostRate, bill: roleRate.hourlyBillRate } : 'NOT FOUND');

    if (!roleRate) {
      console.warn('[BudgetCalculationService] No rate found for role:', roleBudget.role, '- using role fallback rates');
      console.warn('[BudgetCalculationService] Available roles:', roleRates.map(r => r.roleName));
      const fallbackBillRate = roleBudget.billRate || 0;
      const fallbackCostRate = roleBudget.costRate || fallbackBillRate;
      const totalHours = roleBudget.budgetHours || 0;
      const totalCost = totalHours * fallbackCostRate;
      const totalRevenue = totalHours * fallbackBillRate;
      const margin = totalRevenue - totalCost;
      return {
        roleId: roleBudget.role,
        roleName: roleBudget.role,
        totalHours: Math.round(totalHours * 100) / 100,
        totalCost: Math.round(totalCost * 100) / 100,
        totalRevenue: Math.round(totalRevenue * 100) / 100,
        averageCostRate: fallbackCostRate,
        averageBillRate: fallbackBillRate,
        margin: Math.round(margin * 100) / 100,
        monthlyBreakdown: [],
      };
    }

    // SIMPLIFIED: Just use role-level hours × role-level rates
    // Person-level detail is only for display in Rate Card, not for budget calculations
    const totalHours = roleBudget.budgetHours || 0;
    const totalCost = totalHours * roleRate.hourlyCostRate;
    const totalRevenue = totalHours * roleRate.hourlyBillRate;
    const margin = totalRevenue - totalCost;

    console.log('[BudgetCalculationService] Role calculation:', {
      role: roleBudget.role,
      hours: totalHours,
      costRate: roleRate.hourlyCostRate,
      billRate: roleRate.hourlyBillRate,
      totalCost,
      totalRevenue,
      margin
    });

    return {
      roleId: roleBudget.role,
      roleName: roleBudget.role,
      totalHours: Math.round(totalHours * 100) / 100,
      totalCost: Math.round(totalCost * 100) / 100,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      averageCostRate: roleRate.hourlyCostRate,
      averageBillRate: roleRate.hourlyBillRate,
      margin: Math.round(margin * 100) / 100,
      monthlyBreakdown: [],
    };
  }

  /**
   * Calculate budget impact of a rate change
   */
  static calculateRateChangeImpact(
    projectId: string,
    resourceBudget: ResourceBudget,
    roleId: string,
    newCostRate: number,
    newBillRate: number,
    effectiveFrom: string
  ): {
    revenueImpact: number;
    costImpact: number;
    marginImpact: number;
    affectedMonths: string[];
    remainingHours: number;
  } {
    console.log('[BudgetCalculationService] Calculating rate change impact');
    console.log('[BudgetCalculationService] Role:', roleId);
    console.log('[BudgetCalculationService] New rates:', { newCostRate, newBillRate });
    console.log('[BudgetCalculationService] Effective from:', effectiveFrom);

    // Get current calculation
    const currentCalc = this.calculateBudget(projectId, resourceBudget);
    const roleCalc = currentCalc.roleBreakdown.find(r => r.roleName === roleId);

    if (!roleCalc) {
      return {
        revenueImpact: 0,
        costImpact: 0,
        marginImpact: 0,
        affectedMonths: [],
        remainingHours: 0,
      };
    }

    // Calculate impact on future months only (from effectiveFrom date)
    const effectiveDate = new Date(effectiveFrom);
    const affectedMonths: string[] = [];
    let remainingHours = 0;
    let oldCost = 0;
    let oldRevenue = 0;
    let newCost = 0;
    let newRevenue = 0;

    for (const monthCalc of roleCalc.monthlyBreakdown) {
      const monthDate = new Date(monthCalc.month + '-01');
      if (monthDate >= effectiveDate) {
        affectedMonths.push(monthCalc.month);
        remainingHours += monthCalc.hours;
        oldCost += monthCalc.cost;
        oldRevenue += monthCalc.revenue;
        newCost += monthCalc.hours * newCostRate;
        newRevenue += monthCalc.hours * newBillRate;
      }
    }

    const costImpact = newCost - oldCost;
    const revenueImpact = newRevenue - oldRevenue;
    const marginImpact = revenueImpact - costImpact;

    return {
      revenueImpact: Math.round(revenueImpact * 100) / 100,
      costImpact: Math.round(costImpact * 100) / 100,
      marginImpact: Math.round(marginImpact * 100) / 100,
      affectedMonths,
      remainingHours: Math.round(remainingHours * 100) / 100,
    };
  }
}
